import { useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { ArrowDown, ArrowUp, Download } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import DataTable, { Column } from "@/components/ui/DataTable";
import IngredientDetailsDrawer from "@/components/inventory/ingredients/IngredientDetailsDrawer";
import { useGetInventoryWasteQuery } from "@/redux/services/inventory/inventoryReport.api";
import { WasteReportRow } from "@/redux/services/inventory/inventory.type";
import { cn, displayPrice, formatStock } from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import { chartColorPalette } from "@/lib/theme";
import DateRangeControls, {
  DateRangePresetId,
  DateRangeValue,
  computePresetRange,
  previousPeriodRange,
} from "./DateRangeControls";

interface WasteReportProps {
  restaurantId: string;
}

const exportCsv = (
  rows: WasteReportRow[],
  totalCost: number,
  currency: string
) => {
  const header = ["Ingrédient", "Unité", "Quantité perdue", "Coût"];
  const lines = rows.map((r) => [
    r.name,
    r.baseUnit,
    r.wastedQuantity,
    String(r.costImpact / 100),
  ]);
  lines.push(["", "", "Total", String(totalCost / 100)]);
  const csv = [header, ...lines]
    .map((row) =>
      row
        .map((cell) => {
          const v = cell ?? "";
          return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
        })
        .join(",")
    )
    .join("\n");
  const stamp = new Date().toISOString().slice(0, 10);
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `pertes-${stamp}-${currency}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const WasteReport = ({ restaurantId }: WasteReportProps) => {
  const currency = useRestaurantCurrency();
  const [activePreset, setActivePreset] = useState<DateRangePresetId>("30d");
  const [range, setRange] = useState<DateRangeValue>(() =>
    computePresetRange("30d")
  );
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<string>("costImpact");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [drillIngredientId, setDrillIngredientId] = useState<string | null>(
    null
  );

  const { data, isFetching } = useGetInventoryWasteQuery({
    restaurantId,
    query: range,
  });

  const previousRange = useMemo(() => previousPeriodRange(range), [range]);
  const { data: previousData, isFetching: isFetchingPrevious } =
    useGetInventoryWasteQuery(
      { restaurantId, query: previousRange ?? {} },
      { skip: !previousRange }
    );

  const totalCost = data?.totalCost ?? 0;
  const previousTotalCost = previousData?.totalCost ?? 0;
  const totalDelta =
    previousTotalCost > 0
      ? ((totalCost - previousTotalCost) / previousTotalCost) * 100
      : null;

  const rows = data?.rows ?? [];

  const sorted = useMemo(() => {
    const dir = sortOrder === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name) * dir;
        case "wastedQuantity":
          return (parseFloat(a.wastedQuantity) -
            parseFloat(b.wastedQuantity)) * dir;
        case "costImpact":
        default:
          return (a.costImpact - b.costImpact) * dir;
      }
    });
  }, [rows, sortBy, sortOrder]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return sorted;
    return sorted.filter((r) => r.name.toLowerCase().includes(q));
  }, [sorted, search]);

  const top10 = useMemo(
    () => [...rows].sort((a, b) => b.costImpact - a.costImpact).slice(0, 10),
    [rows]
  );

  const chartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        fontFamily: "Inter, sans-serif",
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "70%",
          borderRadius: 4,
        },
      },
      colors: [chartColorPalette[3] ?? chartColorPalette[0]],
      dataLabels: {
        enabled: true,
        formatter: (value: number) => displayPrice(value, currency),
        style: { fontSize: "11px", fontWeight: 500, colors: ["#1f2937"] },
        offsetX: 30,
      },
      xaxis: {
        categories: top10.map((r) => r.name),
        labels: {
          formatter: (val: string) =>
            displayPrice(parseFloat(val) || 0, currency),
        },
      },
      yaxis: {
        labels: { style: { fontSize: "12px" } },
      },
      grid: {
        borderColor: "#e5e7eb",
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
      },
      tooltip: {
        y: {
          formatter: (val: number, { dataPointIndex }) => {
            const row = top10[dataPointIndex];
            const qty = formatStock(row.wastedQuantity, row.baseUnit);
            return `${displayPrice(val, currency)} (${qty})`;
          },
        },
      },
    }),
    [top10, currency]
  );

  const chartSeries = useMemo(
    () => [{ name: "Coût", data: top10.map((r) => r.costImpact) }],
    [top10]
  );

  const columns: Column[] = [
    {
      key: "name",
      label: "Ingrédient",
      sortable: true,
      formatter: (_v, row: WasteReportRow) => (
        <span className="font-medium">{row.name}</span>
      ),
    },
    {
      key: "wastedQuantity",
      label: "Quantité perdue",
      sortable: true,
      formatter: (_v, row: WasteReportRow) =>
        formatStock(row.wastedQuantity, row.baseUnit),
    },
    {
      key: "costImpact",
      label: "Coût",
      sortable: true,
      formatter: (_v, row: WasteReportRow) =>
        displayPrice(row.costImpact, currency),
    },
    {
      key: "share",
      label: "% du coût",
      formatter: (_v, row: WasteReportRow) => {
        if (totalCost === 0) return "—";
        const pct = (row.costImpact / totalCost) * 100;
        return (
          <span className="text-muted-foreground">{pct.toFixed(1)}%</span>
        );
      },
    },
  ];

  const handlePresetChange = (
    next: DateRangeValue,
    preset: DateRangePresetId
  ) => {
    setRange(next);
    setActivePreset(preset);
  };

  return (
    <div className="space-y-4">
      <div className="dash__card">
        <DateRangeControls
          value={range}
          activePreset={activePreset}
          onChange={handlePresetChange}
        />
      </div>

      <div className="dash__card flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">
            Coût total des pertes
          </p>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-2xl font-semibold text-card-foreground">
              {displayPrice(totalCost, currency)}
            </p>
            {totalDelta !== null && !isFetchingPrevious && (
              <span
                className={cn(
                  "inline-flex items-center gap-0.5 text-xs font-medium px-2 py-0.5 rounded-full",
                  totalDelta > 0
                    ? "bg-red-50 text-red-700"
                    : totalDelta < 0
                    ? "bg-green-50 text-green-700"
                    : "bg-muted text-muted-foreground"
                )}
                title={`Période précédente : ${displayPrice(
                  previousTotalCost,
                  currency
                )}`}
              >
                {totalDelta > 0 ? (
                  <ArrowUp className="h-3 w-3" />
                ) : totalDelta < 0 ? (
                  <ArrowDown className="h-3 w-3" />
                ) : null}
                {Math.abs(totalDelta).toFixed(1)}%
              </span>
            )}
          </div>
          {previousRange && !isFetchingPrevious && (
            <p className="text-[11px] text-muted-foreground mt-0.5">
              vs période précédente :{" "}
              {displayPrice(previousTotalCost, currency)}
            </p>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => exportCsv(rows, totalCost, currency)}
          disabled={rows.length === 0}
        >
          <Download className="h-4 w-4" />
          Exporter (CSV)
        </Button>
      </div>

      {isFetching ? (
        <Skeleton className="w-full h-60" />
      ) : (
        <>
          {top10.length > 0 && (
            <div className="dash__card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-card-foreground">
                  Top {top10.length} par coût
                </h3>
                <span className="text-xs text-muted-foreground">
                  Sur la période sélectionnée
                </span>
              </div>
              <ReactApexChart
                options={chartOptions}
                series={chartSeries}
                type="bar"
                height={Math.max(320, top10.length * 44)}
              />
            </div>
          )}

          <DataTable
            data={filtered}
            columns={columns}
            totalCount={filtered.length}
            searchable={true}
            searchValue={search}
            onSearch={setSearch}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={(col, order) => {
              setSortBy(col);
              setSortOrder(order);
            }}
            paginated={false}
            emptyMessage="Aucune perte sur la période sélectionnée"
            onRowClick={(row: WasteReportRow) =>
              setDrillIngredientId(row.ingredientUuid)
            }
          />
        </>
      )}

      <IngredientDetailsDrawer
        isOpen={!!drillIngredientId}
        onClose={() => setDrillIngredientId(null)}
        restaurantId={restaurantId}
        ingredientId={drillIngredientId}
      />
    </div>
  );
};

export default WasteReport;
